/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type ConfigQueryResults,
  type CoreService,
  type CustomBatchSimulateColumns,
  type FlowRunLogs,
  type ListResultsQuery,
  type NetBenefitAnalysisColumns,
  type NetBenefitAnalysisRequest,
  type PlantSimConfiguration,
  type PlantSimDatabaseResultsEntry,
  type PlantSimFlowParameters,
  type PlantSimRequest,
  type PlantSimRequestWithName,
  type PlantTimeseriesTimeframes,
  parsePlantSimTsResults,
  plantSimTsTimeframes,
  type ScenarioRecord,
  type ScenarioWorkflow,
  type Task,
} from '@poweralpha/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import {
  api,
  getQueryConfigsSearchParams,
  getResult,
  getServiceSearchParams,
  type QueryConfigsSearchParams,
  type ServiceListResultsSearchParams,
} from '../api';
import type { ConfigType } from '../react-components/configs/models';
import type { MutationOptions } from '../react-components/queries';
import type { SimulateBatchParamsForm } from './models';

export const runPlantSimSimulation = async (data: PlantSimFlowParameters): Promise<Task> =>
  api.post('plant/simulate', { json: data }).json();

export const runPlantSimBatch = async (form: FormData): Promise<Task> =>
  api.post('plant/batch', { body: form }).json();

export const runPlantSimNetBenefitAnalysis = async (
  request: NetBenefitAnalysisRequest,
): Promise<Task> => api.post('plant/net-benefit', { json: request }).json();

export const parseNetBenefitResults = async (
  reportName: string | undefined,
  statistic?: string,
): Promise<NetBenefitAnalysisColumns> =>
  api
    .get('plant/net-benefit/parse', {
      searchParams: { reportName: reportName ?? '', statistic: statistic ?? '' },
    })
    .json();
export const getPlantSimulationLogs = async (): Promise<FlowRunLogs[]> =>
  api.get('plant/logs').json();

export const getPlantSimResults = async (
  serviceName: CoreService,
  opts?: ServiceListResultsSearchParams,
): Promise<ListResultsQuery<PlantSimDatabaseResultsEntry>> =>
  api
    .get('results', {
      searchParams: {
        serviceName,
        resultFlowName: 'PlantSim',
        ...getServiceSearchParams(opts),
      },
    })
    .json();

export const parsePlantSimBatchFile = async (
  fileId: string | undefined | null,
): Promise<CustomBatchSimulateColumns> => api.get(`plant/batch/${fileId}/parse`).json();

export const getPlantSimTsResult = async (
  id: string | undefined,
  timeframes: Record<keyof PlantTimeseriesTimeframes, boolean>,
): Promise<Record<keyof PlantTimeseriesTimeframes, string>> =>
  api.get(`plant/results/${id}/ts`, { searchParams: timeframes }).json();

export const getPlantSimConfigurations = async <T = PlantSimConfiguration>(
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<T>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return api.get('plant/configs', { searchParams }).json();
};

export const getPlantSimConfiguration = async (
  id: string | undefined | null,
  version: number | undefined,
): Promise<PlantSimConfiguration> =>
  api
    .get(`plant/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<PlantSimConfiguration>();

export const savePlantSimConfiguration = async (
  request: PlantSimRequestWithName,
): Promise<PlantSimConfiguration> => api.post('plant/configs', { json: request }).json();

export const editPlantSimConfiguration = async (
  request: PlantSimRequestWithName,
  id: string | undefined | null,
  version: number,
): Promise<PlantSimConfiguration> =>
  api.post(`plant/configs/${id}/${version}`, { json: request }).json();

export const getPlantSimMaxVersion = async (id: string | undefined | null): Promise<number> =>
  api.get(`plant/configs/${id}/max-version`).json();

export const getPlantSimTsResults = async (
  id: string | undefined,
  timeframes: Array<keyof PlantTimeseriesTimeframes> = plantSimTsTimeframes,
): Promise<Partial<PlantTimeseriesTimeframes>> => {
  const framesObj = Object.fromEntries(
    plantSimTsTimeframes.map((frame) => [frame, timeframes.includes(frame)]),
  ) as Record<keyof PlantTimeseriesTimeframes, boolean>;
  const stringData = await getPlantSimTsResult(id, framesObj);

  const results = Object.entries(stringData).map(([timeframe, str]) => {
    const is12x24s = timeframe.includes('12x24');
    return parsePlantSimTsResults(str, is12x24s);
  });

  return Object.fromEntries(timeframes.map((t, i) => [t, results[i]]));
};

export const usePlantSimTsResultsQuery = (
  id: string | undefined,
  timeframes: Array<keyof PlantTimeseriesTimeframes> = plantSimTsTimeframes,
) =>
  useQuery<Partial<PlantTimeseriesTimeframes>>({
    queryKey: ['plant-sim-ts-results', id],
    queryFn: () => getPlantSimTsResults(id, timeframes),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const usePlantSimResultsQuery = (id: string | undefined) =>
  useQuery<ScenarioRecord | null>({
    queryKey: ['plant-sim-results-record', id],
    queryFn: () =>
      getResult(
        id,
        { plant_sim: true, pv_sim: true, ess_costing: true, workflow: true, project: true },
        'scenario_id',
        false,
      ),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const usePlantSimScenarioQuery = (scenarioId: string | undefined | null) =>
  useQuery({
    queryKey: ['plantsim-scenario-query', scenarioId],
    queryFn: () =>
      getResult<{ request: PlantSimRequest; workflow: ScenarioWorkflow }>(
        scenarioId,
        { 'plant_sim.request': true, workflow: true },
        'scenario_id',
      ),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(scenarioId),
  });

export const usePlantsimConfigQuery = (id: string | null | undefined, version?: number) =>
  useQuery<PlantSimConfiguration>({
    queryKey: ['plantsim-config' satisfies `${ConfigType}-config`, id, version],
    queryFn: () => getPlantSimConfiguration(id ?? null, version),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const useParsePlantSimBatchFileQuery = (id: string | undefined | null) =>
  useQuery<CustomBatchSimulateColumns>({
    queryKey: ['parsed-plant-batch-file', id],
    queryFn: () => parsePlantSimBatchFile(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

interface BatchCountResponse {
  count: number | null;
}

export const useFetchBatchCounts = (
  opts?: MutationOptions<BatchCountResponse, HTTPError, SimulateBatchParamsForm>,
) =>
  useMutation({
    mutationFn: (data) => api.post('proxy/plantsim/batch/counter', { json: data }).json(),
    ...opts,
  });
